/**
 * Provisionamento Device Owner do app zaper-tablet (kiosk de cardápio).
 *
 * O QR gerado aqui é lido pela tela de boas-vindas do Android (6 toques → leitor
 * de QR) e faz o tablet baixar o APK e ativar o app como Device Owner, já amarrado
 * à loja (subdomínio + mesa + PIN + modo de tela vão no ADMIN_EXTRAS_BUNDLE, que o
 * ZaperDeviceAdminReceiver.onProfileProvisioningComplete grava nas prefs "config").
 *
 * Dois valores mudam raramente e ficam aqui como padrão:
 *  - component: só muda se renomearmos o pacote/receiver do app.
 *  - signatureChecksum: SHA-256 do certificado de assinatura (Base64 URL-safe).
 *    Só muda se trocarmos o keystore de release. PREENCHER com a saída do keystore.
 * A URL do APK aponta pro release "latest" no GitHub (estável entre versões).
 */

export const PROVISIONING_DEFAULTS = {
    // package/classe do DeviceAdminReceiver (forma totalmente qualificada)
    component:
        'br.com.zapermenu.zaperautoatendimento/br.com.zapermenu.zaperautoatendimento.ZaperDeviceAdminReceiver',

    // URL HTTPS estável do APK release assinado (GitHub Releases "latest").
    apkUrl:
        'https://github.com/diegoluan23/zaper-tablet/releases/latest/download/zaper-tablet.apk',

    // SHA-256 do certificado de assinatura em Base64 URL-safe (sem padding).
    // Gerado por scripts/gen-keystore.ps1 do zaper-tablet (keystore zaper-release).
    // Só muda se trocarmos o keystore de release.
    signatureChecksum: 'HcwRa667zT_Tky0yD6bsGb8SUu8N8dYzQcms9WAck5Q',
};

/** Standby de brilho: padrão do app quando o QR não manda nada. */
export const STANDBY_DEFAULTS = {
    enabled: true,
    minutes: 15,
};

export const SCREEN_MODES = [
    { value: 'ALWAYS_ON', text: 'Tela sempre acesa (vitrine, na tomada)' },
    { value: 'ON_WHEN_CHARGING', text: 'Acesa só carregando' },
    { value: 'WAKE_ON_TOUCH', text: 'Acende no toque (autoatendimento)' },
];

export const WIFI_SECURITY_TYPES = [
    { value: 'NONE', text: 'Sem Wi-Fi no QR (conectar na mão)' },
    { value: 'WPA', text: 'WPA/WPA2' },
    { value: 'WEP', text: 'WEP' },
];

/** Gera um PIN de admin de 6 dígitos por dispositivo. */
export function generateAdminPin() {
    return String(Math.floor(100000 + Math.random() * 900000));
}

/**
 * URL final do cardápio que o tablet vai abrir — espelha a lógica do MainActivity
 * (full_url ausente): serve só para o operador conferir antes de gerar o QR.
 */
export function tabletMenuUrl(subdomain, deviceCode) {
    const sub = (subdomain || '').trim();
    const dev = (deviceCode || '').trim();
    if (!sub) return 'https://app.zapermenu.com.br/tablet';
    if (!dev) return `https://app.zapermenu.com.br/${sub}/tablet`;
    return `https://app.zapermenu.com.br/${sub}/tablet/${dev}`;
}

/**
 * Monta o objeto de provisionamento (as chaves android.app.extra.*) a serializar
 * como JSON dentro do QR.
 */
export function buildProvisioningPayload(opts) {
    const {
        subdomain,
        deviceCode,
        adminPin,
        screenMode,
        standbyEnabled,
        standbyMinutes,
        wifiSsid,
        wifiPassword,
        wifiSecurity,
        component,
        apkUrl,
        signatureChecksum,
    } = opts;

    // Todos os extras vão como String: é assim que o app lê (getString) os valores do
    // PersistableBundle do QR.
    const extras = {};
    if (subdomain && subdomain.trim()) extras.subdomain = subdomain.trim();
    if (deviceCode && deviceCode.trim()) extras.device_code = deviceCode.trim();
    if (adminPin) extras.admin_pin = String(adminPin).trim();
    if (screenMode) extras.screen_mode = screenMode;
    if (standbyEnabled != null) extras.standby_enabled = String(!!standbyEnabled);
    if (standbyMinutes) extras.standby_minutes = String(standbyMinutes);

    const payload = {
        'android.app.extra.PROVISIONING_DEVICE_ADMIN_COMPONENT_NAME':
            component || PROVISIONING_DEFAULTS.component,
        'android.app.extra.PROVISIONING_DEVICE_ADMIN_PACKAGE_DOWNLOAD_LOCATION':
            apkUrl || PROVISIONING_DEFAULTS.apkUrl,
        'android.app.extra.PROVISIONING_DEVICE_ADMIN_SIGNATURE_CHECKSUM':
            signatureChecksum || PROVISIONING_DEFAULTS.signatureChecksum,
        // Mantém apps de sistema (Configurações/Wi-Fi) disponíveis no Modo Admin.
        'android.app.extra.PROVISIONING_LEAVE_ALL_SYSTEM_APPS_ENABLED': true,
        // Pula criptografia forçada => provisionamento mais rápido.
        'android.app.extra.PROVISIONING_SKIP_ENCRYPTION': true,
        'android.app.extra.PROVISIONING_ADMIN_EXTRAS_BUNDLE': extras,
    };

    // Wi-Fi opcional: pré-conecta durante o setup (dispensa digitar no tablet).
    if (wifiSsid && wifiSsid.trim() && wifiSecurity && wifiSecurity !== 'NONE') {
        payload['android.app.extra.PROVISIONING_WIFI_SSID'] = wifiSsid.trim();
        payload['android.app.extra.PROVISIONING_WIFI_SECURITY_TYPE'] = wifiSecurity;
        if (wifiPassword) {
            payload['android.app.extra.PROVISIONING_WIFI_PASSWORD'] = wifiPassword;
        }
    }

    return payload;
}
